import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Bot, User, Trash2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Robot mascot icon
const ROBOT_ICON = "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/67fab65f02be6b766ed7e9a5a868d2e9a2f5db32d2c9edb603c968ef09ab7cd2.png";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load session from localStorage
  useEffect(() => {
    const savedSessionId = localStorage.getItem('chat_session_id');
    if (savedSessionId) {
      setSessionId(savedSessionId);
      loadChatHistory(savedSessionId);
    }
  }, []);

  const loadChatHistory = async (sid) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const res = await axios.get(`${BACKEND_URL}/api/chat-support/history/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(res.data.messages);
      }
    } catch (error) {
      // Session might have expired - that's okay
      console.log('No previous chat history');
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    
    if (!inputValue.trim() || isLoading) return;

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/chat-support/send`,
        {
          message: userMessage.content,
          session_id: sessionId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Save session ID
      if (res.data.session_id) {
        setSessionId(res.data.session_id);
        localStorage.setItem('chat_session_id', res.data.session_id);
      }

      // Add assistant message
      const assistantMessage = {
        role: 'assistant',
        content: res.data.message,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: "I'm having trouble connecting. Please try again or email hello@aileadmantra.com",
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!sessionId) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/chat-support/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to clear history:', error);
    }

    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('chat_session_id');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-[#0a5c44] shadow-lg flex items-center justify-center hover:bg-[#0a6b50] transition-all ${isOpen ? 'scale-0' : 'scale-100'}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: isOpen ? 0 : 1 }}
      >
        <img 
          src={ROBOT_ICON} 
          alt="Chat Support" 
          className="w-10 h-10"
        />
        {/* Pulse animation */}
        <span className="absolute w-full h-full rounded-full bg-emerald-400 animate-ping opacity-20" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-widget-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : '500px'
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200"
          >
            {/* Header */}
            <div className="bg-[#0a5c44] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src={ROBOT_ICON} 
                  alt="AI Assistant" 
                  className="w-10 h-10"
                />
                <div>
                  <h3 className="text-white font-semibold">AI Support</h3>
                  <p className="text-emerald-200 text-xs">Powered by AILeadMantra</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {messages.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="p-1.5 hover:bg-emerald-700/50 rounded-lg transition-colors"
                    title="Clear chat"
                  >
                    <Trash2 className="w-4 h-4 text-emerald-200" />
                  </button>
                )}
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-emerald-700/50 rounded-lg transition-colors"
                  title={isMinimized ? 'Expand' : 'Minimize'}
                >
                  <Minimize2 className="w-4 h-4 text-emerald-200" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 hover:bg-emerald-700/50 rounded-lg transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4 text-emerald-200" />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="h-80 overflow-y-auto p-4 bg-gray-50">
                  {messages.length === 0 && (
                    <div className="text-center py-8">
                      <Bot className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                      <h4 className="font-semibold text-gray-700 mb-1">Hi there!</h4>
                      <p className="text-sm text-gray-500">
                        I'm your AI assistant. Ask me anything about AILeadMantra!
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2 justify-center">
                        {['How to add leads?', 'Create a funnel', 'Set up automation'].map((q) => (
                          <button
                            key={q}
                            onClick={() => {
                              setInputValue(q);
                            }}
                            className="px-3 py-1.5 text-xs bg-emerald-100 text-emerald-700 rounded-full hover:bg-emerald-200 transition-colors"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.role === 'user' ? 'bg-[#0a5c44]' : 'bg-emerald-100'
                        }`}>
                          {msg.role === 'user' ? (
                            <User className="w-4 h-4 text-white" />
                          ) : (
                            <Bot className="w-4 h-4 text-emerald-600" />
                          )}
                        </div>
                        <div className={`rounded-2xl px-4 py-2 ${
                          msg.role === 'user' 
                            ? 'bg-[#0a5c44] text-white rounded-tr-sm' 
                            : 'bg-white text-gray-700 rounded-tl-sm shadow-sm border border-gray-100'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <span className={`text-xs mt-1 block ${
                            msg.role === 'user' ? 'text-emerald-200' : 'text-gray-400'
                          }`}>
                            {formatTime(msg.timestamp)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Typing indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-start gap-2"
                    >
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-gray-100">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendMessage} className="p-4 bg-white border-t border-gray-100">
                  <div className="flex gap-2">
                    <input
                      data-testid="chat-input"
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type your message..."
                      disabled={isLoading}
                      className="flex-1 px-4 py-2 bg-gray-100 rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm disabled:opacity-50"
                    />
                    <Button
                      data-testid="chat-send-btn"
                      type="submit"
                      disabled={!inputValue.trim() || isLoading}
                      className="bg-[#0a5c44] hover:bg-[#0a6b50] text-white rounded-xl px-4 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
